import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';
import type React from 'react';
import {
  Duration5Icon,
  Duration6Icon,
  Duration10Icon,
  Duration15Icon,
  EnhanceIcon,
  JPGIcon,
  ModeFunIcon,
  ModeNormalIcon,
  ModeSpicyIcon,
  PNGIcon,
  Resolution1KIcon,
  Resolution2KIcon,
  Resolution4KIcon,
  Resolution480Icon,
  Resolution720Icon,
  Resolution1080Icon,
  TOOLTIP_CLASSES,
  Var1Icon,
  Var2Icon,
  Var3Icon,
  Var4Icon,
} from './ControlIcons';

interface SecondaryControlsProps {
  model: string;
  resolution: string;
  duration: string;
  outputFormat: string;
  variations: string;
  mode: string;
  activeDropdown: string | null;
  toggleDropdown: (name: string) => void;
  handleSelect: (type: string, val: string, label: string) => void;
  setOpenSection: (section: string | null) => void;
  highlighted: Record<string, boolean>;
  enhanceContent?: React.ReactNode;
}

export function SecondaryControls({
  model,
  resolution,
  duration,
  outputFormat,
  variations,
  mode,
  activeDropdown,
  toggleDropdown,
  handleSelect,
  setOpenSection,
  highlighted,
  enhanceContent,
}: SecondaryControlsProps) {
  // Trigger Icons Mapping
  const ResolutionButtonIcon = () => {
    if (resolution === '480p') return <Resolution480Icon />;
    if (resolution === '720p') return <Resolution720Icon />;
    if (resolution === '1080p') return <Resolution1080Icon />;
    if (resolution === '1K') return <Resolution1KIcon />;
    if (resolution === '2K') return <Resolution2KIcon />;
    if (resolution === '4K') return <Resolution4KIcon />;
    return <Resolution1080Icon />;
  };

  const DurationButtonIcon = () => {
    if (duration === '5s') return <Duration5Icon />;
    if (duration === '6s') return <Duration6Icon />;
    if (duration === '10s') return <Duration10Icon />;
    if (duration === '15s') return <Duration15Icon />;
    return <Duration10Icon />;
  };

  const FormatButtonIcon = () => {
    if (outputFormat === 'jpg') return <JPGIcon />;
    if (outputFormat === 'png') return <PNGIcon />;
    return <JPGIcon />;
  };

  const VariationsButtonIcon = () => {
    const v = Number.parseInt(variations);
    if (v === 1) return <Var1Icon />;
    if (v === 2) return <Var2Icon />;
    if (v === 3) return <Var3Icon />;
    if (v === 4) return <Var4Icon />;
    return <Var1Icon />;
  };

  const ModeButtonIcon = () => {
    if (mode === 'normal') return <ModeNormalIcon />;
    if (mode === 'fun') return <ModeFunIcon />;
    if (mode === 'spicy') return <ModeSpicyIcon />;
    return <ModeNormalIcon />;
  };

  return (
    <div className="ml-auto flex flex-row flex-nowrap items-center gap-[6px]">
      {model !== 'elevenlabs-sfx' && model !== 'z-image' && (
        <div className="control-group controls-right relative">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className={cn('custom-select', highlighted.resolution && 'pulse-highlight')}
                onClick={() => toggleDropdown('resolution')}
                aria-label="Resolution"
              >
                <span className="select-icon">
                  <ResolutionButtonIcon />
                </span>
                <span className="select-arrow">
                  <ChevronDown
                    className={`h-3 w-3 transition-transform ${activeDropdown === 'resolution' ? 'rotate-180' : ''}`}
                  />
                </span>
              </button>
            </TooltipTrigger>
            <TooltipContent className={TOOLTIP_CLASSES} sideOffset={8} showArrow={false}>
              {resolution} Resolution
            </TooltipContent>
          </Tooltip>
          {activeDropdown === 'resolution' && (
            <div className="select-options active">
              {model === 'nano-banana-2' ? (
                <>
                  <div
                    className={cn('option', resolution === '1K' && 'selected')}
                    onClick={() => handleSelect('resolution', '1K', '1K')}
                  >
                    <Resolution1KIcon size={24} /> 1K
                  </div>
                  <div
                    className={cn('option', resolution === '2K' && 'selected')}
                    onClick={() => handleSelect('resolution', '2K', '2K')}
                  >
                    <Resolution2KIcon size={24} /> 2K
                  </div>
                  <div
                    className={cn('option', resolution === '4K' && 'selected')}
                    onClick={() => handleSelect('resolution', '4K', '4K')}
                  >
                    <Resolution4KIcon size={24} /> 4K
                  </div>
                </>
              ) : (
                <>
                  {model !== 'sora-2' && model !== 'sora-2-pro' && model !== 'runway' && (
                    <div
                      className={cn('option', resolution === '480p' && 'selected')}
                      onClick={() => handleSelect('resolution', '480p', '480p')}
                    >
                      <Resolution480Icon size={24} /> 480p
                    </div>
                  )}
                  <div
                    className={cn('option', resolution === '720p' && 'selected')}
                    onClick={() => handleSelect('resolution', '720p', '720p')}
                  >
                    <Resolution720Icon size={24} /> 720p
                  </div>
                  {model !== 'grok-imagine' && (
                    <div
                      className={cn('option', resolution === '1080p' && 'selected')}
                      onClick={() => handleSelect('resolution', '1080p', '1080p')}
                    >
                      <Resolution1080Icon size={24} /> 1080p
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Duration */}
      {model !== 'elevenlabs-sfx' && model !== 'nano-banana-2' && model !== 'z-image' && (
        <div className="control-group controls-right relative">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className={cn('custom-select', highlighted.duration && 'pulse-highlight')}
                onClick={() => toggleDropdown('duration')}
                aria-label="Duration"
              >
                <span className="select-icon">
                  <DurationButtonIcon />
                </span>
                <span className="select-arrow">
                  <ChevronDown
                    className={`h-3 w-3 transition-transform ${activeDropdown === 'duration' ? 'rotate-180' : ''}`}
                  />
                </span>
              </button>
            </TooltipTrigger>
            <TooltipContent className={TOOLTIP_CLASSES} sideOffset={8} showArrow={false}>
              {duration.includes('seconds') ? duration : duration.replace('s', ' seconds')} Duration
            </TooltipContent>
          </Tooltip>
          {activeDropdown === 'duration' && (
            <div className="select-options active">
              {model === 'runway' && (
                <div
                  className={cn('option', duration === '5s' && 'selected')}
                  onClick={() => handleSelect('duration', '5s', '5 seconds')}
                >
                  <Duration5Icon size={24} />
                  <div className="flex flex-col">
                    <span>5 seconds</span>
                    <span className="font-normal text-[9px] text-zinc-400 leading-tight">
                      10–15 words speech
                    </span>
                  </div>
                </div>
              )}
              {model === 'grok-imagine' && (
                <div
                  className={cn('option', duration === '6s' && 'selected')}
                  onClick={() => handleSelect('duration', '6s', '6 seconds')}
                >
                  <Duration6Icon size={24} />
                  <div className="flex flex-col">
                    <span>6 seconds</span>
                    <span className="font-normal text-[9px] text-zinc-400 leading-tight">
                      15–20 words speech
                    </span>
                  </div>
                </div>
              )}
              <div
                className={cn('option', duration === '10s' && 'selected')}
                onClick={() => handleSelect('duration', '10s', '10 seconds')}
              >
                <Duration10Icon size={24} />
                <div className="flex flex-col">
                  <span>10 seconds</span>
                  <span className="font-normal text-[9px] text-zinc-400 leading-tight">
                    25–35 words speech
                  </span>
                </div>
              </div>
              {model !== 'runway' && (
                <div
                  className={cn('option', duration === '15s' && 'selected')}
                  onClick={() => handleSelect('duration', '15s', '15 seconds')}
                >
                  <Duration15Icon size={24} />
                  <div className="flex flex-col">
                    <span>15 seconds</span>
                    <span className="font-normal text-[9px] text-zinc-400 leading-tight">
                      35–50 words speech
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Output Format */}
      {(model === 'nano-banana-2' || model === 'z-image') && (
        <div className="control-group controls-right relative">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className={cn('custom-select', highlighted.outputFormat && 'pulse-highlight')}
                onClick={() => toggleDropdown('outputFormat')}
                aria-label="Output Format"
              >
                <span className="select-icon">
                  <FormatButtonIcon />
                </span>
                <span className="select-arrow">
                  <ChevronDown
                    className={`h-3 w-3 transition-transform ${activeDropdown === 'outputFormat' ? 'rotate-180' : ''}`}
                  />
                </span>
              </button>
            </TooltipTrigger>
            <TooltipContent className={TOOLTIP_CLASSES} sideOffset={8} showArrow={false}>
              {outputFormat.toUpperCase()} Format
            </TooltipContent>
          </Tooltip>
          {activeDropdown === 'outputFormat' && (
            <div className="select-options active">
              <div
                className={cn('option', outputFormat === 'jpg' && 'selected')}
                onClick={() => handleSelect('outputFormat', 'jpg', 'JPG')}
              >
                <JPGIcon size={24} /> JPG
              </div>
              <div
                className={cn('option', outputFormat === 'png' && 'selected')}
                onClick={() => handleSelect('outputFormat', 'png', 'PNG')}
              >
                <PNGIcon size={24} /> PNG
              </div>
            </div>
          )}
        </div>
      )}

      {/* Variations */}
      {model !== 'elevenlabs-sfx' && (
        <div className="control-group controls-right relative">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className={cn('custom-select', highlighted.variations && 'pulse-highlight')}
                onClick={() => toggleDropdown('variations')}
                aria-label="Variations"
              >
                <span className="select-icon">
                  <VariationsButtonIcon />
                </span>
                <span className="select-arrow">
                  <ChevronDown
                    className={`h-3 w-3 transition-transform ${activeDropdown === 'variations' ? 'rotate-180' : ''}`}
                  />
                </span>
              </button>
            </TooltipTrigger>
            <TooltipContent className={TOOLTIP_CLASSES} sideOffset={8} showArrow={false}>
              {variations}{' '}
              {model === 'nano-banana-2' || model === 'z-image'
                ? Number.parseInt(variations) > 1
                  ? 'Image Variations'
                  : 'Image Variation'
                : Number.parseInt(variations) > 1
                  ? 'Video Variations'
                  : 'Video Variation'}
            </TooltipContent>
          </Tooltip>
          {activeDropdown === 'variations' && (
            <div className="select-options active">
              {[1, 2, 3, 4].map((n) => {
                const VarIcon = [Var1Icon, Var2Icon, Var3Icon, Var4Icon][n - 1] || Var1Icon;
                return (
                  <button
                    key={n}
                    type="button"
                    className={cn(
                      'option w-full text-left',
                      variations === String(n) && 'selected'
                    )}
                    onClick={() => handleSelect('variations', String(n), String(n))}
                  >
                    <VarIcon size={24} />
                    {n}{' '}
                    {n > 1
                      ? model === 'nano-banana-2' || model === 'z-image'
                        ? 'images'
                        : 'videos'
                      : model === 'nano-banana-2' || model === 'z-image'
                        ? 'image'
                        : 'video'}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Enhance Dropdown Trigger */}
      {model !== 'elevenlabs-sfx' && (
        <div className="control-group controls-right relative">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className={cn('custom-select', highlighted.enhance && 'pulse-highlight')}
                onClick={() => {
                  toggleDropdown('settings');
                  if (activeDropdown !== 'settings') {
                    setOpenSection(null);
                  }
                }}
                aria-haspopup="listbox"
                aria-expanded={activeDropdown === 'settings'}
                aria-label="Prompt Presets"
              >
                <span className="select-icon">
                  <EnhanceIcon />
                </span>
                <span className="select-arrow">
                  <ChevronDown
                    className={`h-3 w-3 transition-transform ${activeDropdown === 'settings' ? 'rotate-180' : ''}`}
                  />
                </span>
              </button>
            </TooltipTrigger>
            <TooltipContent className={TOOLTIP_CLASSES} sideOffset={8} showArrow={false}>
              Enhance Prompt
            </TooltipContent>
          </Tooltip>
          {enhanceContent}
        </div>
      )}
    </div>
  );
}
